'use client';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { FC, ReactNode, useState } from 'react';

import { getPublicAssetUrl } from '@/general/helpers/getPublicAssetUrl';
import { Button } from 'src/general/components/Button/Button';
import { Image } from 'src/general/components/Image/Image';
import { useFormStepTransition } from 'src/general/transitions/FormStepTransition/FormStepTransition.context';

type CarMatchStartPageProps = {
  microcopy: {
    content: ReactNode;
    buttonLabel: ReactNode;
  };
};

const CarMatch = dynamic(() => import('src/general/animations/CarMatch').then((mod) => mod.CarMatch), {
  ssr: false,
});

export const CarMatchStartPage: FC<CarMatchStartPageProps> = ({ microcopy }) => {
  const { content, buttonLabel } = microcopy;
  const { navigate } = useFormStepTransition();
  const [animationReady, setAnimationReady] = useState<boolean>(false);

  const h1Classes = [
    '[&>h1]:mb-2',
    '[&>h1]:text-center',
    '[&>h1]:text-2xl',
    '[&>h1]:leading-8',
    '[&>h1]:font-extrabold',
    '[&>h1]:text-tex-def-tie1',
  ];

  const pClasses = [
    '[&>p]:mb-6',
    '[&>p]:text-center',
    '[&>p]:text-lg',
    '[&>p]:leading-2xl',
    '[&>p]:font-normal',
    '[&>p]:text-tex-def-tie1',
  ];

  const baseClasses = ['mt-8'];

  const pLastChild = ['[&>p:last-child]:mb-0'];

  const contentContainerClass = [...baseClasses, ...h1Classes, ...pClasses, ...pLastChild].join(' ');

  return (
    <section className="mdp:-mt-10 xl:mt-20">
      <div className="relative mx-auto aspect-3/2 w-full max-w-2xl [&>svg]:block">
        <CarMatch play loop={false} onLoad={() => setAnimationReady(true)} segments={[60, 310]} />
        <Image
          src={getPublicAssetUrl('/images/components/CarMatchStartPage/CarMatchStart.png')}
          alt=""
          width={640}
          height={464}
          priority
          className={clsx(
            'absolute top-0 left-0 h-auto w-full transition-opacity duration-200',
            animationReady ? 'opacity-0' : 'opacity-100',
          )}
        />
      </div>
      <div className={contentContainerClass}>{content}</div>
      <div className="mt-10 mr-auto mb-0 ml-auto w-full md:max-w-44">
        <Button
          variant="primary"
          size="small"
          className="w-full"
          onClick={() => navigate('/car-match/start')}
          data-testid="car-match-start-get-started-button"
        >
          {buttonLabel}
        </Button>
      </div>
    </section>
  );
};
